import { useApolloClient, useMutation } from '@apollo/client';
import { compact, isNil, uniqBy } from 'lodash';
import moment from 'moment';
import { useCallback, useContext } from 'react';
import { RRule } from 'rrule';
import { Mutations, Queries } from '@shared/api';
import { useCardPaginationActions } from '@shared/api/providers/cardPagination';
import { CardChannelContext } from '@shared/components/blocks/contexts/channel';
import { Fields } from '@shared/components/blocks/types';
import { extractAllFields, omitCommonFields } from '@shared/components/blocks/utils';
import { useToast } from '@shared/components/toast';
import {
  useCardDefinition,
  useRemoveCard,
  useResolveCardDefinition,
} from '@shared/scenes/cards/hooks';
import { useChannelEventHandlers } from '@shared/scenes/workflows/hooks';
import { RECURRENCE_CARD_COUNT } from '@shared/styles';
import { API } from '@shared/types/api';
import { useChannel, useChannelUsers } from '@shared/util/hooks';
import { i18n } from '@shared/util/i18n';
import { getTimezone } from '@shared/util/timezone';
import { prepareIdInput } from '@shared/util/workflow';

type CreateFunction = (
  cardDefinitionId: string | undefined,
  workflowTemplateId: string | undefined,
  fields: Fields,
) => Promise<API.CardFragment | undefined>;

export type BatchFieldsItem = Fields & { cardDefinitionId?: string; workflowTemplateId?: string };

type CreateBatchFunction = (fields: BatchFieldsItem[]) => Promise<API.CardFragment[] | undefined>;

type GenerateCardsFunction = (
  workflowTemplateId: string,
  fields: Fields,
) => Array<Fields & { workflowTemplateId: string }>;

type UpdateFunction = (
  card: API.CardFragment,
  fields: Fields,
) => Promise<API.CardFragment | undefined>;

function useInsertCardInList(channelId: number | undefined): (card: API.CardFragment) => void {
  const apolloClient = useApolloClient();
  return useCallback(
    (card: API.CardFragment) => {
      try {
        const data = apolloClient.readQuery<API.ListCardsQuery, API.ListCardsQueryVariables>({
          query: Queries.card.listCards,
          variables: { input: { channelId } },
        });

        if (!!data?.list.cards) {
          apolloClient.writeQuery<API.ListCardsQuery, API.ListCardsQueryVariables>({
            data: {
              list: {
                ...data.list,
                cards: uniqBy([card, ...data.list.cards], 'id'),
              },
            },
            query: Queries.card.listCards,
            variables: { input: { channelId } },
          });
        }
      } catch (error) {
        // Apollo just crashes if readQuery doesn't find a query in the local cache
      }
    },
    [channelId, apolloClient],
  );
}

function useAddCardDetailsQueryToCache(): (card: API.CardFragment) => void {
  const apolloClient = useApolloClient();
  return useCallback(
    (card: API.CardFragment) => {
      apolloClient.writeQuery<API.CardDetailsQuery, API.CardDetailsQueryVariables>({
        data: { card },
        query: Queries.card.details,
        variables: { id: card.id },
      });
    },
    [apolloClient],
  );
}

interface CreateCardInputProps {
  channelId: number;
  cardDefinitionId?: string;
  workflowTemplateId?: string;
  fields: Fields;
  ignoreRecurrence?: boolean;
  cardDefinition: API.CardDefinitionFragment;
}

function createCardInput({
  channelId,
  cardDefinitionId,
  workflowTemplateId,
  fields,
  ignoreRecurrence,
  cardDefinition,
}: CreateCardInputProps): API.CreateCard | API.CreateNonRecurringCard {
  const customFields = omitCommonFields(fields, cardDefinition, !isNil(workflowTemplateId));

  return {
    channelId,
    cardDefinitionId: prepareIdInput(cardDefinitionId),
    workflowTemplateId: prepareIdInput(workflowTemplateId),
    startDate: fields.startDate,
    endDate: fields.endDate,
    fields: customFields,
    kanbanSortKey: Date.now().toString(),
    cardReminders: !!fields.reminder?.length ? fields.reminder : null,
    ...(!ignoreRecurrence && {
      entityRecurrence: !!fields.recurrence ? { rule: fields.recurrence } : null,
    }),
    assigneeIds: fields.assignees,
    urgent: fields.urgent || false,
    timezone: getTimezone(),
  };
}

export function useCreateCardMutation(
  channelId: number | undefined,
  cardDefinitionId?: string,
): CreateFunction {
  const [createCard] = useMutation<API.CreateCardMutation, API.CreateCardMutationVariables>(
    Mutations.cards.create,
  );
  const channel = useChannel({ channelId });
  const { cardDefinition } = useCardDefinition(
    channel?.id,
    channel?.workflowTemplateId,
    cardDefinitionId,
  );

  return useCallback<CreateFunction>(
    async (callbackCardDefinitionId, workflowTemplateId, fields) => {
      if (!channelId || !cardDefinition) {
        return undefined;
      }
      const input = createCardInput({
        channelId,
        cardDefinitionId: callbackCardDefinitionId,
        workflowTemplateId,
        fields,
        cardDefinition,
      });
      const { data } = await createCard({
        variables: {
          input,
        },
      });

      return data?.createCard;
    },
    [createCard, channelId, cardDefinition],
  );
}

export function useCreateBatchCardsMutation(channelId: number | undefined): CreateBatchFunction {
  const [createCards] = useMutation<
    API.CreateBatchCardsMutation,
    API.CreateBatchCardsMutationVariables
  >(Mutations.cards.createBatchCards);
  const channel = useChannel({ channelId });
  const { cardDefinition } = useCardDefinition(channel?.id, channel?.workflowTemplateId);

  return useCallback<CreateBatchFunction>(
    async cards => {
      if (!channelId || !cardDefinition) {
        return undefined;
      }
      const { data } = await createCards({
        variables: {
          input: {
            cards: cards.map(fields =>
              createCardInput({
                channelId,
                cardDefinitionId: fields?.cardDefinitionId,
                workflowTemplateId: fields?.workflowTemplateId,
                fields,
                ignoreRecurrence: true,
                cardDefinition,
              }),
            ),
          },
        },
      });

      return data?.createCards;
    },
    [createCards, channelId, cardDefinition],
  );
}

function useUpdateCardInCache(channelId: number | undefined) {
  const insertCardInList = useInsertCardInList(channelId);
  const pagination = useCardPaginationActions();
  const addCardDetailsQueryToCache = useAddCardDetailsQueryToCache();

  return useCallback(
    (card: API.CardFragment) => {
      insertCardInList(card);
      pagination.addCard(card);
      addCardDetailsQueryToCache(card);
    },
    [addCardDetailsQueryToCache, insertCardInList, pagination],
  );
}

export function useCreateBatchCards(): CreateBatchFunction {
  const channel = useContext(CardChannelContext);
  const channelId = channel?.id;
  const updateCardInCache = useUpdateCardInCache(channelId);
  const toast = useToast();
  const createCardsMutation = useCreateBatchCardsMutation(channelId);

  return useCallback<CreateBatchFunction>(
    async cards => {
      if (!channelId) {
        return undefined;
      }
      const createdCards = await createCardsMutation(cards);
      if (createdCards) {
        createdCards.forEach(updateCardInCache);
        toast?.show({
          icon: 'check-filled',
          message: i18n(`chat.groupedSystemMessages.CARD_CREATED`, { count: cards.length }),
        });
      }
      return createdCards;
    },
    [channelId, createCardsMutation, toast, updateCardInCache],
  );
}

export function useCreateCard(cardDefinitionId?: string): CreateFunction {
  const channel = useContext(CardChannelContext);
  const channelId = channel?.id;
  const updateCardInCache = useUpdateCardInCache(channelId);
  const toast = useToast();
  const createCardMutation = useCreateCardMutation(channelId, cardDefinitionId);
  const { onOpenCard } = useChannelEventHandlers();

  return useCallback<CreateFunction>(
    async (cardDefId, workflowTemplateId, fields) => {
      if (!channelId) {
        return undefined;
      }
      const card = await createCardMutation(cardDefId, workflowTemplateId, fields);
      if (!!card) {
        updateCardInCache(card);
        toast?.show({
          icon: 'check-filled',
          actions: [
            {
              label: 'view',
              onPress: () => onOpenCard(card, true),
            },
          ],
          message: 'Card created!',
        });
      }
      return card;
    },
    [channelId, createCardMutation, updateCardInCache, toast, onOpenCard],
  );
}

export function useGenerateCardsForRecurrence(): GenerateCardsFunction {
  return useCallback<GenerateCardsFunction>((workflowTemplateId, fields) => {
    const cards: Array<Fields & { workflowTemplateId: string }> = [];
    const rrule = RRule.fromString(fields.recurrence);
    const closestRecurrenceDate = getClosestRecurrenceDate(rrule);
    const offset = calculateOffsetInSec(fields.startDate, closestRecurrenceDate);
    rrule.all((date: Date, num: number): boolean => {
      const startDate = moment(date).add(offset, 'second').toDate();
      const card = { ...fields, workflowTemplateId, recurrence: null, startDate };
      cards.push(card);
      return num < RECURRENCE_CARD_COUNT - 1;
    });
    return cards;
  }, []);
}

function getClosestRecurrenceDate(rrule: RRule): Date {
  const now = moment().startOf('day').toDate();
  const closestRecurrence = rrule.before(now, true);
  if (isNil(closestRecurrence)) {
    return rrule.after(now)!; // assert that this never returns null
  } else {
    return closestRecurrence;
  }
}

function calculateOffsetInSec(_laterDate: Date | string, earlierDate: Date): number {
  const laterDate = _laterDate instanceof Date ? _laterDate : new Date(_laterDate);
  const offset = Math.floor((laterDate.getTime() - earlierDate.getTime()) / 1000);
  return offset < 0 ? 0 : offset;
}

export function getUpdateCardInput(
  card: API.CardFragment,
  fields: Fields,
  cardDefinition: API.CardDefinitionFragment,
  isNoCode: boolean,
): API.UpdateCard {
  const customFields = omitCommonFields(fields, cardDefinition, isNoCode);

  return {
    id: card.id,
    name: fields.name,
    startDate: fields.startDate || null,
    endDate: fields.endDate || null,
    fields: customFields,
    kanbanSortKey: fields.kanbanSortKey,
    assigneeIds: fields.assignees || [],
    cardReminders: fields.reminder ?? [],
    entityRecurrence: (() => {
      if (fields.recurrenceUpdateScope === 'ENTITY') {
        return undefined;
      }
      if (!!fields.recurrence) {
        return { rule: fields.recurrence };
      }
      return null;
    })(),
    scope: fields.recurrenceUpdateScope,
  };
}

export function useOptmisticCard() {
  const channel = useContext(CardChannelContext);
  const { users } = useChannelUsers({ channelId: channel?.id });

  return useCallback(
    (card: API.CardFragment, input: API.UpdateCard): API.CardFragment => {
      return {
        ...card,
        startDate: input.startDate ?? null,
        endDate: input.endDate ?? null,
        kanbanSortKey: input.kanbanSortKey ?? card.kanbanSortKey,
        fields: input.fields ?? null,
        assignees: compact((input.assigneeIds ?? []).map(id => users.find(u => u.id === id))),
        reminders:
          input.cardReminders?.map(r => ({
            conditionalSelector: null,
            ...r,
            __typename: 'CardReminder',
          })) || null,
        recurrence:
          !!input.entityRecurrence && !!card.recurrence
            ? ({
                ...card.recurrence,
                ...input.entityRecurrence,
              } as API.CardRecurrenceFragment)
            : null,
      };
    },
    [users],
  );
}

export function useUpdateCardLocally() {
  const pagination = useCardPaginationActions();
  const getOptmisticCard = useOptmisticCard();
  const resolveCardDefinition = useResolveCardDefinition();

  return useCallback(
    (card: API.CardFragment, fields: Fields) => {
      const cardDefinition = resolveCardDefinition(card.cardDefinitionId ?? undefined);
      if (!cardDefinition) {
        return;
      }
      const input = getUpdateCardInput(
        card,
        fields,
        cardDefinition,
        !isNil(card.workflowTemplateId),
      );
      const optimisticCard = getOptmisticCard(card, input);
      pagination?.updateCard(optimisticCard);
    },
    [getOptmisticCard, pagination, resolveCardDefinition],
  );
}

export function useUpdateCardMutation(): UpdateFunction {
  const pagination = useCardPaginationActions();
  const [updateCardMutation] = useMutation<API.UpdateCardMutation, API.UpdateCardMutationVariables>(
    Mutations.cards.update,
  );
  const getOptmisticCard = useOptmisticCard();
  const resolveCardDefinition = useResolveCardDefinition();

  return useCallback<UpdateFunction>(
    async (card, fields) => {
      try {
        const cardDefinition = resolveCardDefinition(card.cardDefinitionId ?? undefined);
        if (!cardDefinition) {
          return undefined;
        }
        const input = getUpdateCardInput(
          card,
          fields,
          cardDefinition,
          !isNil(card.workflowTemplateId),
        );
        const optimisticCard = getOptmisticCard(card, input);
        pagination?.updateCard(optimisticCard);

        const { data } = await updateCardMutation({
          variables: { input },
          optimisticResponse: () => ({ updateCard: optimisticCard }),
        });

        const updatedCard = data?.updateCard;
        if (updatedCard) {
          pagination?.updateCard(updatedCard);
        }
        return updatedCard;
      } catch {
        console.warn('card not updated');
        return undefined;
      }
    },
    [getOptmisticCard, pagination, updateCardMutation, resolveCardDefinition],
  );
}

export function useUpdateCard(): UpdateFunction {
  const updateCard = useUpdateCardMutation();
  const createCard = useCreateCard();
  const removeCard = useRemoveCard();
  return useCallback<UpdateFunction>(
    async (card, fields) => {
      const shouldSplitRecurrence =
        !!card.recurrence &&
        !!fields.recurrence &&
        card.recurrence.rule !== fields.recurrence &&
        fields.recurrenceUpdateScope === 'ENTITY_UPCOMING';

      if (shouldSplitRecurrence) {
        await removeCard(card.id, 'ENTITY_UPCOMING');
        return createCard(
          card.cardDefinitionId ?? undefined,
          card.workflowTemplateId ?? undefined,
          fields,
        );
      } else {
        return updateCard(card, fields);
      }
    },
    [removeCard, createCard, updateCard],
  );
}

export function useDuplicateCard(): (cardId: string) => Promise<void> {
  const createCard = useCreateCard();
  const apolloClient = useApolloClient();
  const resolveCardDefinition = useResolveCardDefinition();

  return useCallback(
    async (cardId: string) => {
      const { data } = await apolloClient.query<
        API.CardDetailsQuery,
        API.CardDetailsQueryVariables
      >({
        query: Queries.card.details,
        variables: { id: cardId },
      });
      const card = data?.card;
      if (!card) {
        return;
      }
      const cardDefinition = resolveCardDefinition(card.cardDefinitionId ?? undefined);
      if (!cardDefinition?.definition) {
        return;
      }
      const fields = extractAllFields(card, cardDefinition.definition);
      await createCard(
        card.cardDefinitionId ?? undefined,
        card.workflowTemplateId ?? undefined,
        fields,
      );
    },
    [apolloClient, resolveCardDefinition, createCard],
  );
}

type DeleteBatchFunction = (cardIds: string[]) => Promise<boolean | undefined>;

export function useDeleteBatchCardsMutation(): DeleteBatchFunction {
  const [deleteCards] = useMutation<
    API.DeleteBatchCardsMutation,
    API.DeleteBatchCardsMutationVariables
  >(Mutations.cards.deleteBatchCards);

  return useCallback<DeleteBatchFunction>(
    async ids => {
      const { data } = await deleteCards({
        variables: {
          input: {
            ids,
          },
        },
      });

      return data?.deleteCards ?? undefined;
    },
    [deleteCards],
  );
}

export function useDeleteBatchCards(): DeleteBatchFunction {
  const pagination = useCardPaginationActions();
  const toast = useToast();
  const deleteCards = useDeleteBatchCardsMutation();

  return useCallback<DeleteBatchFunction>(
    async cardIds => {
      const success = await deleteCards(cardIds);
      if (success) {
        cardIds.forEach(cardId => pagination?.removeCard(cardId, true));
        toast?.show({
          icon: 'check-filled',
          message: i18n(`chat.groupedSystemMessages.CARD_DELETED`, { count: cardIds.length }),
        });
      }
      return success;
    },
    [deleteCards, pagination, toast],
  );
}
